import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { useCart } from 'hooks/use-cart';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { getEvaluationResultMessage } from 'state/loyalty/hooks/utils/helpers';

import { ICartContinueErrorOptions } from './types';

export const useCartContinueErrors = ({ cartPriceTooLow }: { cartPriceTooLow: boolean }) => {
  const incentiveEvaluationErrors = useAppSelector(state =>
    selectors.loyalty.selectIncentiveErrorsWithCode(
      state,
      IncentiveEvaluationErrorCodes.INVALID_PAYMENT_METHOD
    )
  );
  const { cartEntries } = useCart();

  // This method returns an array to handle other errors in the future
  // similar to `getErrorMessages`
  const getLoyaltyPaymentErrorMessages = () => {
    if (!incentiveEvaluationErrors.length) {
      return [];
    }

    return [
      {
        messageId: '' as TranslationKey,
        message: getEvaluationResultMessage({
          evaluationResult: incentiveEvaluationErrors[0],
          incentiveType: LoyaltyPromotionType.OFFER,
          cartEntries,
        }),
      },
    ];
  };

  const getErrorMessages = ({
    areDeliveryDetailsWithError,
    invalidAddressLine2,
    cartPriceTooLow: serverOrderCartPriceTooLow,
    checkoutPaymentMethodNeedRevaultError,
    invalidVehicleDescription,
    invalidOfferError,
    invalidPaymentMethodError,
    invalidReloadError,
    invalidTipAmountError,
    noDirectPayAndNoPrepaidMethodError,
    pricedAndAvailableError,
    storeIsClosing,
    unableToReloadCardError,
    unableToProcessOrderError,
  }: ICartContinueErrorOptions) => {
    const cartContinueErrors: Array<{
      hasError: boolean;
      message: string;
      messageId: TranslationKey;
    }> = [
      {
        message: t('invalidVehicleDescription'),
        messageId: 'invalidVehicleDescription',
        hasError: invalidVehicleDescription ?? false,
      },
      {
        message: t('invalidOfferError'),
        messageId: 'invalidOfferError',
        hasError: invalidOfferError ?? false,
      },
      {
        message: t('invalidPaymentMethodError'),
        messageId: 'invalidPaymentMethodError',
        hasError: invalidPaymentMethodError ?? false,
      },
      {
        message: t('invalidTipAmountError'),
        messageId: 'invalidTipAmountError',
        hasError: invalidTipAmountError ?? false,
      },
      {
        message: t('minimumPriceError'),
        messageId: 'minimumPriceError',
        hasError: !!cartPriceTooLow || !!serverOrderCartPriceTooLow,
      },
      {
        message: t('unableToReloadCardError'),
        messageId: 'unableToReloadCardError',
        hasError: unableToReloadCardError ?? false,
      },
      {
        message: t('pricedAndAvailableError'),
        messageId: 'pricedAndAvailableError',
        hasError: pricedAndAvailableError ?? false,
      },
      {
        message: t('areDeliveryDetailsValid'),
        messageId: 'areDeliveryDetailsValid',
        hasError: areDeliveryDetailsWithError ?? false,
      },
      {
        message: t('invalidAddressLine2'),
        messageId: 'invalidAddressLine2',
        hasError: invalidAddressLine2 ?? false,
      },
      {
        message: t('noDirectPayAndNoPrepaidMethodError'),
        messageId: 'noDirectPayAndNoPrepaidMethodError',
        hasError: noDirectPayAndNoPrepaidMethodError ?? false,
      },
      {
        message: t('invalidReloadError'),
        messageId: 'invalidReloadError',
        hasError: invalidReloadError ?? false,
      },
      {
        message: t('checkoutPaymentMethodNeedRevaultError'),
        messageId: 'checkoutPaymentMethodNeedRevaultError',
        hasError: checkoutPaymentMethodNeedRevaultError ?? false,
      },
      {
        message: t('storeIsClosingError'),
        messageId: 'storeIsClosingError',
        hasError: storeIsClosing ?? false,
      },
      {
        message: t('couldntProcessOrder'),
        messageId: 'couldntProcessOrder',
        hasError: unableToProcessOrderError ?? false,
      },
    ];

    const cartContinueErrorMessages = cartContinueErrors.flatMap(item =>
      item.hasError ? [{ ...item }] : []
    );
    return cartContinueErrorMessages;
  };

  return {
    getErrorMessages,
    getLoyaltyPaymentErrorMessages,
  };
};
